import {
  Autocomplete,
  Box,
  Chip,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import { t } from "i18next";
import CustomButton from "../components/customComponent/CustomButton";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { CloudIcon } from "lucide-react";
import { useRecoilState, useResetRecoilState } from "recoil";
import {
  selectedFileState,
  serverFileSecondState,
  tempFileState,
} from "../recoil";
import ServerFileModal from "../components/ServerFileModal";
import { ServerModalStyle } from "../styles/GlobalStyles";
import { useNavigate, useParams } from "react-router-dom";
import {
  flattenTransformedData,
  transformData,
} from "../utils/datasetFileUtil";
import { RenderFileChips } from "../utils/utilsComponent";
import {
  createDatasetInfo,
  editDataset,
  editDatasetFile,
  getDatasetInfo,
  getFile,
} from "common-lib";

export default function DatasetCreatePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  const [openDataset, setOpenDataset] = useState<boolean>(false);
  const [serverFiles, setServerFiles] = useRecoilState(serverFileSecondState);
  const resetServerFiles = useResetRecoilState(serverFileSecondState);
  const [serverFilesNames, setServerFilesNames] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useRecoilState(selectedFileState);
  const [tempFiles, setTempFiles] = useRecoilState(tempFileState); // (오른쪽) 데이터셋으로 설정할 선택된 이미지들 - 확정 전
  const resetTempFiles = useResetRecoilState(tempFileState);

  const [formData, setFormData] = useState<DatasetFormItem>({
    name: "",
    description: null,
    type: "stsup-cls",
    classList: [],
    files: { unlabeled: [] },
  });

  useEffect(() => {
    return () => {
      resetTempFiles();
      resetServerFiles();
    };
  }, [resetTempFiles, resetServerFiles]);

  useEffect(() => {
    const fetchFiles = async () => {
      if (isEditMode) {
        const datasetInfo = await getDatasetInfo(Number(id));
        console.log("datasetInfo", datasetInfo.data);
        const infoData = datasetInfo.data;

        setFormData({
          name: infoData.name,
          description: infoData.description,
          type: infoData.type,
          classList: infoData.classList,
          // files: datasetFile.files ?? {},
          files: {},
        });
        const datasetFile = await getFile(infoData.path);
        const formattedData = flattenTransformedData(
          transformData(datasetFile.data)
        );

        setTempFiles(formattedData);
        setServerFiles(formattedData);
        const paths: string[] = formattedData.map((item) => item.path);
        setServerFilesNames(paths);
      }
    };
    fetchFiles();
  }, [id, isEditMode]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
    field: string
  ) => {
    let value = e.target.value;

    setFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent, field: string) => {
    const value = e.target.value;

    setFormData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleClassChange = (event: React.SyntheticEvent, value: string[]) => {
    setFormData((prevState) => ({
      ...prevState,
      classList: value,
    }));
  };

  // const handleSubmit = async () => {
  //   // classList와 unlabeled를 포함한 초기 그룹 객체 생성
  //   const groupedFiles: Record<string, string[]> = {};
  //   formData.classList.forEach((label) => (groupedFiles[label] = []));
  //   groupedFiles["unlabeled"] = [];

  //   // serverFiles를 순회하면서 해당 label에 파일 추가
  //   serverFiles.forEach((file) => {
  //     if (groupedFiles[file.label]) {
  //       groupedFiles[file.label].push(file.path);
  //     } else if (file.label === "unlabeled") {
  //       groupedFiles["unlabeled"].push(file.path);
  //     }
  //   });

  //   console.log("save", {
  //     ...formData,
  //     classList: formData.classList,
  //     files: groupedFiles,
  //   });

  //   if (formData.name === "") {
  //     toast.warn("데이터셋 이름을 입력해주세요.");
  //     return;
  //   }

  //   try {
  //     const response = await createDatasetInfo({
  //       ...formData,
  //       files: groupedFiles,
  //     });
  //     console.log(response);
  //   } catch (error) {
  //     console.error(error);
  //   }
  // };

  const handleSubmit = async () => {
    // serverFiles 기준으로 groupedFiles 생성
    let groupedFiles = serverFiles.reduce((acc, file) => {
      if (
        formData.classList.includes(file.label) ||
        file.label === "unlabeled"
      ) {
        if (!acc[file.label]) {
          acc[file.label] = [];
        }
        acc[file.label].push(file.path);
      }
      return acc;
    }, {} as Record<string, string[]>);

    // unlabeled는 무조건 포함
    if (!groupedFiles["unlabeled"]) {
      groupedFiles["unlabeled"] = [];
    }

    // classList 기준 key도 보장
    formData.classList.forEach((cls) => {
      if (!groupedFiles[cls]) {
        groupedFiles[cls] = [];
      }
    });

    console.log("save", {
      ...formData,
      classList: formData.classList,
      files: groupedFiles,
    });

    if (formData.name === "") {
      toast.warn("데이터셋 이름을 입력해주세요.");
      return;
    }

    try {
      await createDatasetInfo({
        ...formData,
        files: groupedFiles,
      });
      navigate("/home");
    } catch (error) {
      console.error(error);
    }
  };

  const handleEdit = async () => {
    const groupedFiles = serverFiles.reduce((acc, file) => {
      // 파일의 label이 classList 안에 있거나 "unlabeled"라면만 처리
      if (
        formData.classList.includes(file.label) ||
        file.label === "unlabeled"
      ) {
        if (!acc[file.label]) {
          acc[file.label] = [];
        }
        acc[file.label].push(file.path);
      }
      return acc;
    }, {} as Record<string, string[]>);

    if (formData.name === "") {
      toast.warn("데이터셋 이름을 입력해주세요.");
      return;
    }

    try {
      const editInfoRes = await editDataset(
        Number(id),
        formData.name,
        formData.description,
        formData.type
      );
      const response = await editDatasetFile(
        Number(id),
        formData.classList,
        groupedFiles
      );
      navigate("/home");
      console.log(editInfoRes, response);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteServerFiles = () => {
    setServerFiles([]);
  };

  return (
    <>
      <Container
        component="main"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "fit-content",
          mt: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "900px",
            height: "fit-content",
            border: "1px solid var(--dk-border, #ccc)",
            borderRadius: "6px",
            backgroundColor: "var(--secondary-bg-color)",
          }}
        >
          <div className="p-8 bg-blue-400">
            <h1 className="mb-2 text-3xl font-bold text-white">
              {t("CreateDataset")}
            </h1>
            <p className="text-blue-100">데이터셋을 구성하세요.</p>
          </div>
          <div className="p-8 space-y-8">
            <div>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Data Set Name"
                placeholder="내용을 입력하세요"
                value={formData.name || ""}
                onChange={(e) => handleInputChange(e, "name")}
                margin="normal"
              />
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Description"
                placeholder="내용을 입력하세요"
                value={formData.description || ""}
                onChange={(e) => handleInputChange(e, "description")}
                margin="normal"
              />
              <Box sx={{ mt: 2 }}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel id="dataset-type-label">Data Set Type</InputLabel>
                  <Select
                    labelId="dataset-type-label"
                    value={formData.type}
                    onChange={(e) => handleSelectChange(e, "type")}
                    label="Data Set Type"
                  >
                    <MenuItem value="stsup-cls">stsup-cls</MenuItem>
                    <MenuItem value="stsup-seg">stsup-seg</MenuItem>
                    <MenuItem value="unsup-cls">unsup-cls</MenuItem>
                    <MenuItem value="unsup-seg">unsup-seg</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <div className="input-container">
                <Autocomplete
                  style={{ borderStyle: "none" }}
                  multiple
                  id="tags-outlined"
                  // options={uuid ? classList : initialClassList}
                  options={["OK", "NG"]}
                  freeSolo
                  filterSelectedOptions
                  getOptionLabel={(option) => option} //옵션값 (선택)
                  onChange={handleClassChange}
                  value={formData.classList || []}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Class List" />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...otherProps } = getTagProps({ index }); // key 분리
                      return <Chip key={key} label={option} {...otherProps} />;
                    })
                  }
                />
                <label htmlFor="input-field">Class List</label>
              </div>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CustomButton
                  text={t("SelectServerFile")}
                  variant="outlined"
                  onClick={() => {
                    setOpenDataset(true);
                  }}
                />
                {serverFiles.length > 0 && (
                  <Box>
                    <RenderFileChips
                      files={serverFiles}
                      icon={<CloudIcon />}
                      handleDelete={handleDeleteServerFiles}
                    />
                  </Box>
                )}
              </Box>
              <Box
                sx={{
                  mt: 1,
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                {openDataset && (
                  <Modal open={openDataset}>
                    <Box sx={ServerModalStyle}>
                      <ServerFileModal onClick={() => setOpenDataset(false)} />
                    </Box>
                  </Modal>
                )}
              </Box>
              <Box
                sx={{
                  mt: 1,
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                {isEditMode ? (
                  <CustomButton
                    text={t("Edit")}
                    variant="contained"
                    onClick={handleEdit}
                  />
                ) : (
                  <CustomButton
                    text={t("Save")}
                    variant="contained"
                    onClick={handleSubmit}
                  />
                )}
              </Box>
            </div>
          </div>
        </Box>
      </Container>
    </>
  );
}
